import {css} from 'aphrodite';
import React from 'react';

import {ValidationStatus} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {
  BulkAssemblyConstructsStructuredTableMetadata,
  BulkAssemblyConstructsStructuredTableRowMetadata,
} from 'molbio/bulk-assembly/tables/BulkAssemblyConstructsStructuredTableAdapter';
import {
  BulkAssemblyFragmentsStructuredTableMetadata,
  BulkAssemblyFragmentsStructuredTableRowMetadata,
} from 'molbio/bulk-assembly/tables/BulkAssemblyFragmentsStructuredTableAdapter';
import {VALIDATION_STATUS_TO_ICON_DETAILS} from 'molbio/bulk-assembly/tables/BulkAssemblyValidationStatus';
import makeCustomButtonActionFunction from 'notebook/structured-tables/action-functions/makeCustomButtonActionFunction';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import {TableActionFunctionParameters} from 'notebook/structured-tables/StructuredTableConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

const VALIDATION_STATUS_TO_COPY: Record<ValidationStatus, string> = {
  [ValidationStatus.SUCCESS]: 'No errors',
  [ValidationStatus.WARNING]: 'Contains warning',
  [ValidationStatus.ERROR]: 'Contains error',
};

export function renderStatusFilterDropdownButton<
  TTableMetadata extends {
    isFilterDisabledByStatus: {[key in ValidationStatus]: boolean};
    statusFilter: ValidationStatus | null;
    onSetStatusFilter: (newStatusFilter: ValidationStatus | null) => void;
  },
  TRowMetadata,
>({structuredTable}: TableActionFunctionParameters<TTableMetadata, TRowMetadata>): JSX.Element {
  const activeFilter = structuredTable.metadata.statusFilter;
  return (
    <Dropdown
      menu={
        <Menu width={MenuWidth.SMALL} value={activeFilter}>
          {[ValidationStatus.SUCCESS, ValidationStatus.WARNING, ValidationStatus.ERROR].map(
            (validationStatus) => {
              const {icon, styleObject} = VALIDATION_STATUS_TO_ICON_DETAILS[validationStatus];
              return (
                <Option
                  isDisabled={
                    structuredTable.metadata.isFilterDisabledByStatus[validationStatus] &&
                    validationStatus !== activeFilter
                  }
                  value={validationStatus}
                  key={validationStatus}
                  onSelect={(newValue: ValidationStatus) => {
                    if (activeFilter === newValue) {
                      // Unselect if filter is currently active.
                      structuredTable.metadata.onSetStatusFilter(null);
                    } else {
                      structuredTable.metadata.onSetStatusFilter(newValue);
                    }
                  }}
                  icon={icon}
                  iconClassName={css(styleObject)}
                >
                  {VALIDATION_STATUS_TO_COPY[validationStatus]}
                </Option>
              );
            }
          )}
        </Menu>
      }
    >
      <Button
        size={ButtonSize.MINI}
        // Mimic how ActionFunctionButton normally separates action buttons.
        className="u-mini-spacing-left"
        isDropdown={true}
        buttonStyle={activeFilter != null ? ButtonStyle.TERTIARY_BLUE_POWDER : ButtonStyle.TERTIARY_GRAY}
        text={activeFilter != null ? `Status: ${VALIDATION_STATUS_TO_COPY[activeFilter]}` : 'Status'}
      />
    </Dropdown>
  );
}

export const statusFilterConstructsActionFunction = makeCustomButtonActionFunction<
  BulkAssemblyConstructsStructuredTableMetadata,
  BulkAssemblyConstructsStructuredTableRowMetadata
>({
  getHidden: () => false,
  renderToolbarButton: renderStatusFilterDropdownButton,
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
});

export const statusFilterFragmentsActionFunction = makeCustomButtonActionFunction<
  BulkAssemblyFragmentsStructuredTableMetadata,
  BulkAssemblyFragmentsStructuredTableRowMetadata
>({
  getHidden: () => false,
  renderToolbarButton: renderStatusFilterDropdownButton,
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
});
